// ─── Snapshots Panel (Phase 1.9) ──────────────────────────────
// Small History/Restore card in the Studio sidebar. Lets the designer:
//   - "Save snapshot" of the current invitation state (optional label)
//   - See the most recent 5 snapshots with label + relative timestamp
//   - Restore a snapshot (confirmation required)
//
// Snapshots are managed entirely server-side; this panel is a thin client.
// Capped at 5 (oldest dropped FIFO) and never exposed on public guest pages.
// Public-facing endpoints strip `layout_snapshots`.

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { History, Save, RotateCcw, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function formatRelative(iso) {
  if (!iso) return '';
  try {
    const t = new Date(iso).getTime();
    const diff = Date.now() - t;
    if (diff < 30_000) return 'just now';
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return 'moments ago';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return '';
  }
}

export default function SnapshotsPanel({ eventId, token, onRestored }) {
  const [open, setOpen] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [label, setLabel] = useState('');

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchSnapshots = useCallback(async () => {
    if (!eventId || !token) return;
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/api/events/${eventId}/rsvp-page/snapshots`,
        { headers: authHeaders },
      );
      setSnapshots(r.data?.snapshots || []);
    } catch (err) {
      // silent — list will stay empty
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, token]);

  useEffect(() => {
    if (open) fetchSnapshots();
  }, [open, fetchSnapshots]);

  const handleSave = useCallback(async () => {
    if (!eventId || !token) return;
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/api/events/${eventId}/rsvp-page/snapshot`,
        { label: (label || '').trim() || 'Manual snapshot' },
        { headers: authHeaders },
      );
      setSnapshots(r.data?.snapshots || []);
      setLabel('');
      toast.success('Snapshot saved');
    } catch (err) {
      toast.error('Could not save snapshot');
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, token, label]);

  const handleRestore = useCallback(async (snapId) => {
    if (!eventId || !token) return;
    setRestoringId(snapId);
    try {
      const r = await axios.post(
        `${API}/api/events/${eventId}/rsvp-page/restore-snapshot/${snapId}`,
        {},
        { headers: authHeaders },
      );
      setSnapshots(r.data?.snapshots || []);
      setConfirmId(null);
      toast.success('Restored');
      // Let parent refetch the rsvp-page and rehydrate `content`.
      onRestored?.();
    } catch (err) {
      toast.error('Restore failed');
    } finally {
      setRestoringId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, token, onRestored]);

  if (!eventId) return null;

  return (
    <div
      className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900"
      data-testid="snapshots-panel"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 group"
        data-testid="snapshots-panel-toggle"
      >
        <span className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300">
          <History className="w-4 h-4" />
          History
          {snapshots.length > 0 && (
            <span className="text-[10px] font-normal text-gray-400">
              {snapshots.length}/5
            </span>
          )}
        </span>
        {open ? (
          <ChevronUp className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        )}
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-2.5 border-t border-gray-100 dark:border-gray-800 pt-2.5">
          {/* Save row */}
          <div className="flex items-center gap-1.5">
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Label (optional)"
              maxLength={60}
              className="flex-1 px-2 py-1.5 text-xs rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200 placeholder:text-gray-400"
              data-testid="snapshots-label-input"
            />
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              data-testid="snapshots-save-btn"
            >
              {saving ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Save className="w-3 h-3" />
              )}
              Save
            </button>
          </div>

          {/* List */}
          {loading ? (
            <div
              className="flex items-center justify-center py-3 text-[11px] text-gray-400"
              data-testid="snapshots-loading"
            >
              <Loader2 className="w-3 h-3 animate-spin mr-1.5" />
              Loading…
            </div>
          ) : snapshots.length === 0 ? (
            <div
              className="text-[11px] text-gray-400 italic py-2"
              data-testid="snapshots-empty"
            >
              No restore points yet. Snapshots are also created automatically on publish.
            </div>
          ) : (
            <ul
              className="space-y-1.5"
              data-testid="snapshots-list"
            >
              {[...snapshots].reverse().map((s) => {
                const isConfirm = confirmId === s.id;
                const isBusy = restoringId === s.id;
                return (
                  <li
                    key={s.id}
                    className="rounded-md border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-2"
                    data-testid={`snapshot-row-${s.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-medium text-gray-700 dark:text-gray-300 truncate">
                          {s.label || 'Snapshot'}
                        </div>
                        <div className="text-[10px] text-gray-400 mt-0.5">
                          {formatRelative(s.createdAt)}
                        </div>
                      </div>
                      {isConfirm ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleRestore(s.id)}
                            disabled={isBusy}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                            data-testid={`snapshot-confirm-restore-${s.id}`}
                          >
                            {isBusy ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <RotateCcw className="w-3 h-3" />
                            )}
                            Confirm
                          </button>
                          <button
                            onClick={() => setConfirmId(null)}
                            disabled={isBusy}
                            className="px-2 py-1 rounded text-[10px] text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                            data-testid={`snapshot-cancel-restore-${s.id}`}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmId(s.id)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                          title="Restore this snapshot"
                          data-testid={`snapshot-restore-btn-${s.id}`}
                        >
                          <RotateCcw className="w-3 h-3" />
                          Restore
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <p
            className="text-[10px] text-gray-400 italic leading-relaxed pt-1"
            data-testid="snapshots-help-text"
          >
            Up to 5 restore points. Restoring auto-saves your current state first, so it&apos;s reversible.
          </p>
        </div>
      )}
    </div>
  );
}
